import { useCookies } from '@/components/composable/composable_cookie'
import { getDateStringYMD } from '@/service/service_date'
import type { ApplicationTreeNode } from '@/service/service_unit'
import type { components, paths } from '@/types/gateway_schema'
import type { paths as paths_supervisor } from '@/types/hypervision_schema'
import createClient from 'openapi-fetch'
import { defineStore } from 'pinia'
import { toast } from 'vue-sonner'

type GatewayStoreState = {
  root?: components['schemas']['SopravisionOrganizationUnitDTO']
}

const client = createClient<paths>({
  baseUrl: '/gateway'
})

const client_supervisor = createClient<paths_supervisor>({
  baseUrl: '/api'
})

export const useOrganizationUnitStore = defineStore('organization_units', {
  state: (): GatewayStoreState => ({
    root: undefined
  }),
  getters: {
    getRoot: (state) => {
      return state.root
    },
    getByID: (state) => {
      return (id: string) => {
        const search = (unit: ApplicationTreeNode): ApplicationTreeNode | null => {
          if (unit.id === id) return unit
          for (let i = 0; i < unit.childrens.length; i++) {
            const result = search(unit.childrens[i])

            if (result) return result
          }

          return null
        }
        if (!state.root) return null
        return search(state.root)
      }
    },
    getJWT: (_) => {
      const { getCookieValue } = useCookies()
      const cookie = getCookieValue('hv_jwt')
      return cookie
    },
    getDeviceById: (state) => {
      return (id: string): components['schemas']['SopravisionDeviceDTO'] | undefined => {
        if (!state.root) return undefined
        const findDeviceRecursive = (
          unit: components['schemas']['SopravisionOrganizationUnitDTO']
        ): components['schemas']['SopravisionDeviceDTO'] | undefined => {
          const deviceFound = unit.devices.find((device) => device.id === id)

          if (deviceFound) {
            return deviceFound
          }

          for (const child of unit.childrens) {
            const foundInChild = findDeviceRecursive(child)
            if (foundInChild) return foundInChild
          }

          return undefined
        }

        return findDeviceRecursive(state.root)
      }
    },
    getOrganizationUnitParameters: (_) => {
      return (node: ApplicationTreeNode) => {
        const parameters: components['schemas']['SupervisorDeviceParameterDTO'][] = []
        const collect = (unit: ApplicationTreeNode) => {
          const device_parameters = unit.devices.map((e) => e.parameters).flat()
          parameters.push(...device_parameters)
          unit.childrens.forEach((e) => {
            collect(e)
          })
        }

        collect(node)
        return parameters
      }
    }
  },
  actions: {
    async fetch() {
      const response = await client.GET('/api/sopravision/organizationUnits', {
        headers: { 'X-AUTH-TOKEN': this.getJWT.value }
      })
      if (response.data) {
        this.root = response.data
      }
    },
    async fetchDashboard(node: ApplicationTreeNode, date: Date) {
      const response = await client.GET('/api/sopravision/organizationUnits/{id}/savings', {
        params: {
          path: {
            id: node.id
          },
          query: {
            date: getDateStringYMD(date)
          }
        },
        headers: { 'X-AUTH-TOKEN': this.getJWT.value }
      })

      return response
    },
    async updateOrganizationUnitParameters(node: ApplicationTreeNode) {
      const parameters = this.getOrganizationUnitParameters(node)
      const promises = []

      for (const parameter of parameters) {
        parameter.updatedBy = undefined
        parameter.updatedAt = undefined
        parameter.createdAt = undefined
        parameter.createdBy = undefined
        parameter.unit = undefined

        const request = client_supervisor.PUT('/api/DeviceParameters/{id}', {
          params: {
            path: {
              id: parameter.deviceParameterId!
            }
          },
          body: parameter,
          headers: {
            Authorization: `Bearer ${this.getJWT.value}`
          }
        })

        promises.push(request)
      }

      return Promise.all(promises)
        .then(() => {
          toast.success('Les paramètres ont été mis à jour.')
        })
        .catch((error) => {
          toast.error(`Une erreur s'est produite :` + error)
        })
    }
  }
})
